
import Vue from "vue";
import { mapState } from "vuex";
import {Contact, EmailAddress} from "@/api/contacts";
import SendMessage, { SendMessageTransfer } from "@/components/SendMessage.vue";
import EventBus from "@/utils/EventBus";
import Navigation from "@/layout/Navigation.vue";
import TopBar, {TabItem} from "@/layout/TopBar.vue";
import Encounter, {EncounterFormDTO} from "@/views/contacts/Encounter.vue";
import {ValidationProvider} from "vee-validate";
import {authApi} from "@/api/auth";
import ConfirmDlg from "@/components/ConfirmDlg.vue";
import ConfirmDlgUser from "@/components/ConfirmDlgWithUser.vue";
import ConfirmDlgBlackBoards  from "@/components/ConfirmDlgWithBlackBoards.vue";

// move events to TopBar!!!
export interface BusiNaviEvent {
  enable: boolean;
}

export interface ResetNaviEvent {
  enable: boolean;
}

export interface ConfigureEmailNaviEvent {
  recipients: EmailAddress[];
}

export interface ConfigureTabNaviEvent {
  tabItems: TabItem[],
  activeTab?: number,
  activeTabByName?: string,
}

export interface LoadingStateEvent {
  loading: boolean;
}

export interface SaveNaviEvent {
  enable: boolean;
  title: string;
}

export interface UploadNaviEvent {
  enable: boolean;
  title: string;
}

export interface AddNaviEvent {
    enable: boolean;
    title: string;
}

export interface SaveEvent {
  enable: boolean;
}


export interface UploadEvent {
  enable: boolean;
}

export interface AddEvent {
    enable: boolean;
}

export interface LeaveEvent {
  enable: boolean;
}

export interface BackOffNaviEvent {
  enable: boolean;
}

export interface SelectedContactEvent {
  contact: Contact | null;
}

export interface SnackbarMsgEvent {
  msg: string,
  severity?: number
}

export interface SendMailToEvent {
  to: string[];
  contact: Contact;
  cc?: string[];
  bcc?: string[];
  prefilledContent: string | null;
  prefilledSubject: string | null;
  basedOnEmailId: string | null;
  disableAutoSignature?: boolean;
  draft?: boolean;
  contactDocumentRefs: string[] | null;
  predefinedDocumentRefs: string[] | null;
  imageGalleryRefs: string[] | null;
}

export default Vue.extend({
  name: "App",
  components: {
    ConfirmDlgBlackBoards,
    ConfirmDlgUser,
    ConfirmDlg,
    ValidationProvider,
    SendMessage,
    Navigation,
    TopBar,
    Encounter
  },
  data: () => ({
    showSendMessageDialog: false,
    showEncounterDialog: false,
    mailboxId: "",
    sendMessageTransfer: null as SendMessageTransfer | null,
    snackbarSeverity: 0,
    snackbar: false,
    snackbarTimeout: 8000,
    snackbarInfos: "",
    encounterForm: null as EncounterFormDTO | null,
    changePasswordDialog: false,
    showPassword: false,
    newPassword: "",
  }),

  computed: {
    ...mapState("mailbox", ["mailboxes"]),
  },
  async mounted() {
    EventBus.$on("sendMailTo", this.sendMailTo);
    EventBus.$on("toggleEmailDialog", this.toggleEmailDialog);
    EventBus.$on("snackbarInfosTo", this.showSnackInfos);
    EventBus.$on("selectedContact", this.selectedContact);
    EventBus.$on("resetNavi", this.resetLayout);
  },
  beforeDestroy() {
    EventBus.$off("sendMailTo", this.sendMailTo);
    EventBus.$off("toggleEmailDialog", this.toggleEmailDialog);
    EventBus.$off("snackbarInfosTo", this.showSnackInfos);
    EventBus.$off("selectedContact", this.selectedContact);
    EventBus.$off("resetNavi", this.resetLayout);
  },
  methods: {
    async changePassword() {
      await authApi.changePassword({ password: this.newPassword });
      this.changePasswordDialog = false;
    },
    resetLayout() {
      this.showEncounterDialog = false;
      if( !this.showSendMessageDialog) {
        this.sendMessageTransfer = null;
      }
    },
    async toggleEmailDialog() {
      if( this.showSendMessageDialog) {
        const res = await (this.$refs.confirmDlg as any).open(
            this.$t("Achtung") as string,
            this.$t(
                "Soll das Fenster zum Versenden der E-Mail geschlossen werden?"
            ) as string,
            {
              textColor: "red",
              textOk: "Ja",
              textCancel: "Nein und zurück",
              noconfirm: false,
            }
        );
        this.showSendMessageDialog = !res;
      }else {
        this.showSendMessageDialog = true;
      }
    },
    toggleEncounterDialog() {
      this.showEncounterDialog = !this.showEncounterDialog;
    },
    selectedContact(event: SelectedContactEvent) {
      if( !this.showSendMessageDialog) {
        this.sendMessageTransfer = null;
      }
      this.encounterForm = {
        contact: event.contact
      } as EncounterFormDTO
      if( !(event.contact && event.contact.id)) {
        this.showEncounterDialog = false;
      }
    },
    showEncounterDialogFor(contact: Contact) {
      if( this.showEncounterDialog ) {
        this.showEncounterDialog = false;
        return;
      }
      this.encounterForm = {
        contact: contact
      } as EncounterFormDTO
      if( !(contact && contact.id)) {
        this.showEncounterDialog = false;
      }else {
        this.showEncounterDialog = true;
      }
    },
    sendMailTo(event: SendMailToEvent) {
      if( !this.showSendMessageDialog) {
        this.sendMessageTransfer = {
          prefilledSubject: event.prefilledSubject ?? "",
          prefilledContent: event.prefilledContent ?? "",
          basedOnEmailId: event.basedOnEmailId ?? "",
          contact: event.contact,
          to: event.to,
          cc: event.cc ?? [],
          bcc: event.bcc ?? [],
          disableAutoSignature: event.disableAutoSignature ?? false,
          draft: event.draft ?? false,
          contactDocumentRefs: event.contactDocumentRefs,
          predefinedDocumentRefs: event.predefinedDocumentRefs,
          imageGalleryRefs: event.imageGalleryRefs
        } as SendMessageTransfer;
        this.showSendMessageDialog = true;
      }
    },
    messageWasSent() {
      this.showSendMessageDialog = false;
    },
    showSnackInfos(message: SnackbarMsgEvent) {
      this.snackbarInfos = message.msg;
      this.snackbarSeverity = message.severity ?? 0
      this.snackbar = true;
    },
  },
});
